import { isEditableElement } from './viewportFrame';
import { holdInlineSpacer, releaseSpacer, inlineSpacerScroller } from '../../utils/ui/inlineKeyboardSpacer';
import { takeOverInlineReveal } from '../../utils/ui/inlineKeyboardGuard';
import { markProgrammaticScroll } from '../../utils/ui/scrollTapGuard';
import { rafScrollTo } from '../../utils/ui/rafScrollTo';
import { findBodyScroller } from './modalHeightFreeze';
import {
  isKeyboardBlurProtected, CONFIRM_CLOSED_MS, PROTECTED_CONFIRM_CLOSED_MS,
} from '../../utils/ui/keyboardDismissBlur';

/**
 * Coordinated keyboard-close choreography for `useModalKeyboardAnticipation`
 * (KAN-552 1c). Plain functions over the hook's refs — extracted from the hook
 * to keep it under the file cap, zero behaviour change.
 */

/**
 * Scrim-dismiss suppression window around ANY anticipation motion (anchor at
 * focus, release glide): the card moving between a tap's press and release can
 * resolve the click against bare scrim and dismiss the modal. 600ms outlives
 * the same-gesture click and the 250ms glide.
 */
export const DISMISS_SUPPRESS_MS = 600;
export const KB_MOTION_MS = 250; // = KB_MOTION's duration
/**
 * ONE-BEAT CLOSE (KAN-930, founder decision 2026-10-22): the close starts this
 * long after `keyboardOpen` flips false — for rest cards AND lifted cards
 * (replaces the old 400ms RELEASE_LINGER_MS, which left a ~300–450ms hold with
 * a half-empty card on the device recording). Tune from a device recording
 * (`?kbdebug=1`): it should land as iOS starts sliding the keyboard away.
 */
export const CLOSE_START_MS = 140;
/*
 * The VISUAL release starts at CLOSE_START_MS, but the field BLUR keeps the
 * KAN-561 confirm windows, measured from when the keyboard closed:
 * CONFIRM_CLOSED_MS (300) for a plain field, PROTECTED_CONFIRM_CLOSED_MS (700)
 * for a password / one-time-code field (iOS swaps keyboard → Strong Password /
 * code panel and reads as closed for a moment). If the keyboard comes back
 * first, the blur is cancelled AND the release is undone (`abort`).
 */
/** Timer (never transitionend — iOS drops end events) that finishes the release. */
export const RELEASE_FALLBACK_MS = KB_MOTION_MS + 80;
/** Keep the guard-style "programmatic scroll" mark alive for the smooth scroll-back. */
const LOAN_SCROLL_MARK_MS = 600;

// Release the guard's spacer ONLY if it is still the one we took over.
export const releaseOurSpacer = (loan) => {
  if (loan && inlineSpacerScroller() === loan.scroller) releaseSpacer();
};

/**
 * Take over the guard's scroll loan for the field's body scroller and HOLD its
 * spacer, so closing is ONE coordinated move instead of three.
 */
export function captureScrollLoan({
  dialogEl, freeze, fieldRef, anchorRef, loanRef, shrink = null,
}) {
  const field = fieldRef.current;
  if (!freeze || !dialogEl || !field || !dialogEl.contains(field)) return;
  // KAN-930: a rest card's body shrink also owns the scroll-back (one driver).
  const shrinking = !!(shrink && shrink.isActive());
  if (!anchorRef.current && !freeze.isFrozen() && !loanRef.current && !shrinking) return;
  const scroller = findBodyScroller(dialogEl, field);
  if (!scroller) return;
  const handoff = takeOverInlineReveal(scroller);
  if (!handoff) return;
  // A re-reveal within the same session keeps the TRUE original position.
  if (!loanRef.current || loanRef.current.scroller !== scroller) {
    loanRef.current = { scroller, originalScrollTop: handoff.beforeTop };
  }
  holdInlineSpacer(scroller);
}

/**
 * @param {object} c
 * @param {HTMLElement} c.dialogEl
 * @param {object|null} c.freeze   createHeightFreeze result
 * @param {object|null} c.tuck     createStepTuck result
 * @param {object|null} [c.shrink] createBodyShrink result while a rest-card shrink is active (KAN-930)
 * @param {object|null} c.anchor   the anchor state at the time the close was scheduled
 * @param {object} c.refs          { loanRef, closingRef, finishTimerRef, cancelScrollRef, suppressUntilRef }
 * @param {Function} c.stopScrollBack
 * @param {Function} c.captureLoan
 * @param {Function} c.setAnchor
 * @param {boolean} [c.blurField=true] - false when the keyboard was never registered open (KAN-646)
 * @param {number} [c.closedAt] - Date.now() when `keyboardOpen` flipped false (blur windows count from it)
 * @returns {{ run: Function, finish: Function, abort: Function, cancelBlur: Function }}
 */
export function createCloseChoreography({
  dialogEl, freeze: f, tuck: tk, anchor, refs, stopScrollBack, captureLoan, setAnchor, blurField = true,
  shrink = null, closedAt = null,
}) {
  const {
    loanRef, closingRef, finishTimerRef, cancelScrollRef, suppressUntilRef,
  } = refs;
  let blurTimer = 0;
  let shrinkScrollTo = null;
  // The typing geometry at `run` — what `abort` returns to.
  let typing = null;
  const cancelBlur = () => { clearTimeout(blurTimer); blurTimer = 0; };

  const finish = () => {
    finishTimerRef.current = 0;
    closingRef.current = false;
    stopScrollBack();
    const loan = loanRef.current;
    loanRef.current = null;
    if (loan) {
      markProgrammaticScroll(loan.scroller, LOAN_SCROLL_MARK_MS);
      // Exact position FIRST, then drop the padding: removing it earlier
      // clamps scrollTop and the list jumps.
      if (loan.scroller.scrollTop !== loan.originalScrollTop) {
        loan.scroller.scrollTop = loan.originalScrollTop;
      }
    }
    releaseOurSpacer(loan);
    if (f) f.finishRelease();
    if (shrink) shrink.finish(shrinkScrollTo); // original inline strings (idempotent)
    setAnchor(null);
  };

  const run = () => {
    // A re-tap in the same session may have made the guard re-reveal: its
    // loan is folded in (our original position stays the truth).
    captureLoan();
    // End the typing session fully: blur the field so the NEXT tap fires a
    // real focusin and re-anchors — a tap on a still-focused field emits
    // no focusin, so the keyboard would re-open with the card centered
    // and iOS would pan (the "re-tap after dismiss" lurch).
    const active = document.activeElement;
    // blurField=false (KAN-646): the keyboard was never registered open, so
    // the user may be typing right now — settle the card, keep the focus.
    // The blur waits out the KAN-561 confirm window from the keyboard's close.
    let blurDelay = 0;
    cancelBlur();
    if (blurField && dialogEl.contains(active) && isEditableElement(active)) {
      const wait = isKeyboardBlurProtected(active) ? PROTECTED_CONFIRM_CLOSED_MS : CONFIRM_CLOSED_MS;
      blurDelay = Math.max(0, (closedAt ?? Date.now()) + wait - Date.now());
      blurTimer = setTimeout(() => {
        blurTimer = 0;
        if (document.activeElement === active) active.blur();
      }, blurDelay);
    }
    const loan0 = loanRef.current;
    typing = {
      height: f && f.isFrozen() ? dialogEl.style.height : null,
      loanTop: loan0 ? loan0.scroller.scrollTop : null,
    };
    // Glide back to EXACTLY where the card rests (stored at anticipation);
    // the (innerHeight − h)/2 fallback ignores the header/pill band. A card
    // whose NATURAL height differs from the one it was lifted at re-centers
    // by half the delta (natural read synchronously, spacer suspended). The
    // bar is read at its final size with the tuck pin released.
    // A tap-rested card's body is shrunk right now: centre it for its UNSHRUNK height (KAN-1181).
    const shrunkNatural = shrink && shrink.isActive() ? shrink.naturalDialogHeight() : null;
    const natural = shrunkNatural != null ? shrunkNatural
      : tk ? tk.measureUnfolded(() => (f && f.naturalHeight()) || dialogEl.offsetHeight)
        : (f && f.naturalHeight()) || dialogEl.offsetHeight;
    const centerTop = anchor && anchor.restingTop != null
      ? anchor.restingTop + Math.round(((anchor.restingH ?? natural) - natural) / 2)
      : Math.max(16, Math.round((window.innerHeight - natural) / 2));
    suppressUntilRef.current = Date.now() + DISMISS_SUPPRESS_MS;
    const loan = loanRef.current;
    if (shrink && shrink.isActive()) {
      // KAN-930 rest card: ONE driver writes body max-height AND scrollTop per
      // frame on the same curve (never a separate scroll loop → no clamp).
      stopScrollBack();
      const owned = loan && shrink.owns(loan.scroller);
      if (owned) markProgrammaticScroll(loan.scroller, LOAN_SCROLL_MARK_MS);
      shrinkScrollTo = owned ? loan.originalScrollTop : null;
      // `keep`: the values stay until `finish`, so `abort` can still undo them.
      shrink.release({ scrollTo: shrinkScrollTo, keep: true });
      if (loan && !owned) cancelScrollRef.current = rafScrollTo(loan.scroller, loan.originalScrollTop, KB_MOTION_MS);
    } else if (loan) {
      markProgrammaticScroll(loan.scroller, LOAN_SCROLL_MARK_MS);
      stopScrollBack();
      cancelScrollRef.current = rafScrollTo(loan.scroller, loan.originalScrollTop, KB_MOTION_MS);
    }
    // The READ happens with the bar unfolded; the height WRITE stays outside it.
    if (f) f.startRelease(tk ? (fn) => tk.measureUnfolded(fn) : undefined);
    // ONE beat: the bar unfold, the tuck pin's tween and the glide all start here.
    if (tk) tk.untuck({ animate: true });
    if (anchor) setAnchor((a) => (a && a.releasing == null ? { ...a, releasing: centerTop } : a));
    clearTimeout(finishTimerRef.current);
    closingRef.current = true;
    // Geometry is only handed back AFTER a pending blur, so `abort` can undo it.
    finishTimerRef.current = setTimeout(finish, Math.max(RELEASE_FALLBACK_MS, blurDelay + 20));
  };

  /**
   * The keyboard came back while the close was still in flight (KAN-561: it
   * read as closed for a moment, e.g. a password field's Strong Password
   * swap). Cancel the pending blur AND the release, and return to the typing
   * geometry on the keyboard curve. Lifted AND rest cards. True when undone.
   */
  const abort = () => {
    if (!closingRef.current || !typing) return false;
    cancelBlur();
    clearTimeout(finishTimerRef.current);
    finishTimerRef.current = 0;
    closingRef.current = false;
    stopScrollBack();
    if (f) f.cancelRelease();
    if (f && typing.height) dialogEl.style.height = typing.height; // the shell's height transition carries it
    const resumed = !!(shrink && shrink.resumeTyping());
    const loan = loanRef.current;
    if (!resumed && loan && typing.loanTop != null) {
      markProgrammaticScroll(loan.scroller, LOAN_SCROLL_MARK_MS);
      cancelScrollRef.current = rafScrollTo(loan.scroller, typing.loanTop, KB_MOTION_MS);
    }
    suppressUntilRef.current = Date.now() + DISMISS_SUPPRESS_MS;
    if (anchor) setAnchor((a) => (a ? { ...a, releasing: undefined, returning: true } : a));
    typing = null;
    return true;
  };

  return { run, finish, abort, cancelBlur };
}
